import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Search, Clear, CheckCircle } from '@mui/icons-material';
import ScriptIcon from '../components/ScriptIcon';
import { apiAppFetch } from '../utils/apiFetch';
import './../AccountPages.css';

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data);
  return [];
};

const QuantitySetting = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState('');
  const [settings, setSettings] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [error, setError] = useState('');
  const [scriptSearch, setScriptSearch] = useState('');

  const loadSettings = async (segment) => {
    if (!segment) {
      setSettings([]);
      return;
    }

    try {
      setLoadingSettings(true);
      setError('');
      const response = await apiAppFetch('getCustomQuantitySetting', {
        method: 'POST',
        body: JSON.stringify({ segment }),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();
      setSettings(normalizeArray(data));
    } catch (fetchError) {
      setError('Unable to load quantity settings right now.');
      setSettings([]);
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    const loadSegments = async () => {
      try {
        setLoadingSegments(true);
        setError('');
        const response = await apiAppFetch('segments', { method: 'GET' });

        if (response.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        const data = await response.json();
        const normalizedSegments = normalizeArray(data);
        setSegments(normalizedSegments);

        if (normalizedSegments.length > 0) {
          const defaultSegment = normalizedSegments[0].segment;
          setSelectedSegment(defaultSegment);
          loadSettings(defaultSegment);
        }
      } catch (fetchError) {
        setError('Unable to load segments right now.');
      } finally {
        setLoadingSegments(false);
      }
    };

    loadSegments();
  }, []);

  const handleSegmentChange = async (segment) => {
    setSelectedSegment(segment);
    setScriptSearch('');
    await loadSettings(segment);
  };

  const filteredSettings = useMemo(() => {
    const searchValue = scriptSearch.trim().toLowerCase();
    if (!searchValue) return settings;
    return settings.filter((item) => {
      const scriptName = String(item?.dispname || '').toLowerCase();
      const lotVariant = String(item?.lotVariant || '').toLowerCase();
      return scriptName.includes(searchValue) || lotVariant.includes(searchValue);
    });
  }, [settings, scriptSearch]);

  return (
    <div className={`account-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="account-search">
        <Search className="account-search-icon" />
        <input
          type="text"
          placeholder="Search script"
          value={scriptSearch}
          onChange={(event) => setScriptSearch(event.target.value)}
          disabled={!selectedSegment}
        />
        {scriptSearch ? (
          <button className="account-search-clear" onClick={() => setScriptSearch('')} type="button">
            <Clear fontSize="small" />
          </button>
        ) : null}
      </div>

      <div className="account-chips">
        {segments.map((segment) => (
          <button
            key={segment.segment}
            type="button"
            className={`account-chip ${selectedSegment === segment.segment ? 'active' : ''}`}
            onClick={() => handleSegmentChange(segment.segment)}
            disabled={loadingSegments}
          >
            {segment.segment_name || segment.segment}
          </button>
        ))}
      </div>

      {(loadingSegments || loadingSettings) && (
        <div className="account-loading">
          <div className="account-spinner" />
          <p>Loading quantity settings...</p>
        </div>
      )}

      {!loadingSegments && !loadingSettings && error && (
        <div className="account-empty">
          <h3>Unable to load data</h3>
          <p>{error}</p>
        </div>
      )}

      {!loadingSegments && !loadingSettings && !error && filteredSettings.length === 0 && (
        <div className="account-empty">
          <CheckCircle />
          <h3>No quantity settings</h3>
          <p>
            {scriptSearch
              ? 'No scripts match your search.'
              : 'No quantity settings found for this segment.'}
          </p>
        </div>
      )}

      {!loadingSegments && !loadingSettings && !error && filteredSettings.length > 0 && (
        <div className="account-panel">
          <div className="account-qty-head">
            <span>Script</span>
            <span className="account-num">Per Pos</span>
            <span className="account-num">Total</span>
          </div>
          {filteredSettings.map((item, index) => (
            <div
              key={`${item.dispname || 'symbol'}-${item.segment || selectedSegment}-${item.lotVariant || 'MEGA'}-${index}`}
              className="account-qty-row"
            >
              <div className="account-row-name">
                <ScriptIcon name={item.dispname} size={26} />
                <span className="script-identity-title">
                  {item.dispname || '-'}
                  {String(item.lotVariant || '').toUpperCase() === 'MINI' && (
                    <span className="lot-variant-mini">MINI</span>
                  )}
                </span>
              </div>
              <div className="account-num">{item.max_order ?? '-'}</div>
              <div className="account-num">{item.positional_lot ?? '-'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuantitySetting;
